import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CrmSidebar } from "@/components/feature/CrmSidebar";
import { useAuth } from "@/components/feature/AuthContext";
import NotificationDropdown from "@/components/feature/NotificationDropdown";
import GlobalSearch from "@/components/feature/GlobalSearch";
import KeyboardShortcutsModal from "@/components/feature/KeyboardShortcutsModal";

type Props = {
  children: React.ReactNode;
  title: string;
};

// Derive breadcrumb from current pathname
function getBreadcrumb(pathname: string, customTitle?: string): { label: string; path?: string }[] | null {
  // Settings sub-pages — parent is /settings
  if (pathname.startsWith("/settings/users")) return [{ label: "Settings", path: "/settings" }, { label: "Users" }];
  if (pathname.startsWith("/settings/branchs")) return [{ label: "Settings", path: "/settings" }, { label: "Branches" }];
  if (pathname.startsWith("/settings/positions")) return [{ label: "Settings", path: "/settings" }, { label: "Positions & Roles" }];
  if (pathname.startsWith("/settings/teams")) return [{ label: "Settings", path: "/settings" }, { label: "Team Structure" }];
  if (pathname.startsWith("/settings/company")) return [{ label: "Settings", path: "/settings" }, { label: "Company Profile" }];
  if (pathname.startsWith("/settings/assignment")) return [{ label: "Settings", path: "/settings" }, { label: "Assignment Rules" }];
  if (pathname.startsWith("/settings/scoring")) return [{ label: "Settings", path: "/settings" }, { label: "Lead Scoring" }];
  if (pathname.startsWith("/settings/industry")) return [{ label: "Settings", path: "/settings" }, { label: "Industry Config" }];
  if (pathname.startsWith("/settings/projects")) return [{ label: "Settings", path: "/settings" }, { label: "Project Settings" }];
  if (pathname.startsWith("/settings/workflows/new")) return [{ label: "Settings", path: "/settings" }, { label: "Workflows", path: "/settings/workflows" }, { label: "New Workflow" }];
  if (pathname.startsWith("/settings/workflows") && pathname.includes("/edit")) return [{ label: "Settings", path: "/settings" }, { label: "Workflows", path: "/settings/workflows" }, { label: "Edit Workflow" }];
  if (pathname.startsWith("/settings/workflows")) return [{ label: "Settings", path: "/settings" }, { label: "Workflows" }];
  if (pathname.startsWith("/settings/ai-agent")) return [{ label: "Settings", path: "/settings" }, { label: "Bot Identity" }];
  if (pathname.startsWith("/settings/knowledge-base")) return [{ label: "Settings", path: "/settings" }, { label: "Knowledge Base" }];
  if (pathname.startsWith("/settings/profile")) return [{ label: "Settings", path: "/settings" }, { label: "My Profile" }];

  // Lead detail page
  const leadMatch = pathname.match(/^\/leads\/(\d+)$/);
  if (leadMatch) {
    return [{ label: "Leads", path: "/leads" }, { label: customTitle || `Lead #${leadMatch[1]}` }];
  }

  // Campaign detail / results page
  const campaignMatch = pathname.match(/^\/campaigns\/([^\/]+)$/);
  if (campaignMatch && pathname !== "/campaigns/new") {
    return [{ label: "Campaigns", path: "/campaigns" }, { label: "Campaign Results" }];
  }

  // Campaign new page
  if (pathname === "/campaigns/new") {
    return [{ label: "Campaigns", path: "/campaigns" }, { label: "New Campaign" }];
  }

  // Quotation detail
  const quotationMatch = pathname.match(/^\/quotations\/(\d+)$/);
  if (quotationMatch && !pathname.includes("/edit")) {
    return [{ label: "Quotations", path: "/quotations" }, { label: `Quotation #${quotationMatch[1]}` }];
  }

  // Project detail
  const projectMatch = pathname.match(/^\/projects\/(.+)$/);
  if (projectMatch) {
    return [{ label: "Projects", path: "/projects" }, { label: "Project Detail" }];
  }

  // Lead Capture pages
  if (pathname === "/lead-capture/new") {
    return [{ label: "Lead Capture", path: "/lead-capture" }, { label: "New Form" }];
  }
  if (pathname.includes("/lead-capture/") && pathname.endsWith("/submissions")) {
    return [{ label: "Lead Capture", path: "/lead-capture" }, { label: "Form Submissions" }];
  }
  const leadCaptureMatch = pathname.match(/^\/lead-capture\/([^\/]+)$/);
  if (leadCaptureMatch && pathname !== "/lead-capture/new") {
    return [{ label: "Lead Capture", path: "/lead-capture" }, { label: "Edit Form" }];
  }

  return null;
}

export default function CrmLayout({ children, title }: Props) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);

  // Sidebar collapse state from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem("crm_sidebar_collapsed") === "1";
  });

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("crm_sidebar_collapsed", next ? "1" : "0");
      return next;
    });
  }, []);

  // "?" shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "?" && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement) && !(e.target instanceof HTMLSelectElement)) {
        e.preventDefault();
        setShortcutsModalOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Close avatar dropdown on outside click
  useEffect(() => {
    if (!avatarMenuOpen) return;
    const handler = () => setAvatarMenuOpen(false);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [avatarMenuOpen]);

  if (!user) return null;

  const initials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const breadcrumb = getBreadcrumb(location.pathname, title);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <CrmSidebar
        mobileMenuOpen={mobileMenuOpen}
        onCloseMobile={() => setMobileMenuOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="flex items-center gap-4 px-5 py-3 border-b border-gray-200 bg-white flex-shrink-0">
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className="ri-menu-line text-xl text-gray-600" />
          </button>

          {/* Left: Page title + breadcrumb */}
          <div className="flex-shrink-0 hidden sm:block">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {breadcrumb && (
              <div className="flex items-center gap-1.5 mt-0.5 text-xs">
                {breadcrumb.map((crumb, i) => (
                  <span key={crumb.label} className="flex items-center gap-1.5">
                    {i > 0 && <span className="text-gray-300">&gt;</span>}
                    {crumb.path ? (
                      <button
                        onClick={() => navigate(crumb.path!)}
                        className="text-gray-400 hover:text-indigo-600 transition-colors cursor-pointer"
                      >
                        {crumb.label}
                      </button>
                    ) : (
                      <span className="text-indigo-600 font-medium">{crumb.label}</span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Center: Global search */}
          <GlobalSearch />

          {/* Right: Notification bell + Avatar */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <NotificationDropdown />

            <div className="hidden sm:flex items-center relative">
              <button
                onClick={(e) => { e.stopPropagation(); setAvatarMenuOpen(!avatarMenuOpen); }}
                className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-xs font-bold text-white cursor-pointer hover:ring-2 hover:ring-indigo-400 transition-all flex-shrink-0"
              >
                {initials}
              </button>

              {/* Avatar dropdown */}
              {avatarMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl border border-gray-200 shadow-lg z-50 py-1 overflow-hidden">
                  <button
                    onClick={() => { navigate("/settings/profile"); setAvatarMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-user-line text-gray-400 w-4 h-4 flex items-center justify-center" />
                    My Profile
                  </button>
                  <button
                    onClick={() => { navigate("/settings"); setAvatarMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-settings-3-line text-gray-400 w-4 h-4 flex items-center justify-center" />
                    Settings
                  </button>
                  <hr className="my-1 border-gray-100" />
                  <button
                    onClick={() => { handleLogout(); setAvatarMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-logout-box-r-line w-4 h-4 flex items-center justify-center" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content area with page transition */}
        <main className="flex-1 overflow-y-auto page-fade-in" key={location.pathname}>
          {children}
        </main>
      </div>

      {/* Keyboard shortcuts modal */}
      {shortcutsModalOpen && (
        <KeyboardShortcutsModal onClose={() => setShortcutsModalOpen(false)} />
      )}
    </div>
  );
}